import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  InputAdornment,
  Typography,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useNavigate } from 'react-router-dom';
import { trackHomeSearch, getSalaryRangeBin, trackCTA } from '../analytics';
import { generateSalaryUrl } from '../utils/salaryRoutes';

/**
 * HeroSearch component - Enhanced UX with visible label, auto-formatting, and live validation
 * Features visible "Annual salary" label, auto-format on blur, and live region validation
 */
const HeroSearch = ({ onSalaryChange }) => {
  const navigate = useNavigate();
  const [salaryInput, setSalaryInput] = useState('');
  const [error, setError] = useState('');
  const [isFormatted, setIsFormatted] = useState(false);

  // Normalize salary input by removing currency symbols, commas, and handling 'k' notation
  const normalizeSalary = (input) => {
    let cleaned = input.replace(/[£,\s]/g, '').toLowerCase();
    
    // Handle 'k' notation (e.g., '45k' becomes '45000')
    if (cleaned.endsWith('k')) {
      const numPart = cleaned.slice(0, -1);
      const number = parseFloat(numPart);
      return isNaN(number) ? null : Math.round(number * 1000);
    }
    
    const number = parseInt(cleaned, 10);
    return isNaN(number) ? null : number;
  };

  // Format salary to £45,000 format
  const formatSalary = (salary) => {
    if (!salary || isNaN(salary)) return '';
    return `${salary.toLocaleString('en-GB')}`;
  };

  // Validate salary amount
  const validateSalary = (salary) => {
    if (salary === null) {
      return 'Please enter a valid salary amount';
    }
    if (salary < 10000 || salary > 500000) {
      return 'Salary must be between £10,000 and £500,000';
    }
    return null;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    const normalizedSalary = normalizeSalary(salaryInput);
    const validationError = validateSalary(normalizedSalary);

    if (validationError) {
      setError(validationError);
      return;
    }

    // Track analytics with privacy-safe salary range and CTA surface
    const salaryRange = getSalaryRangeBin(normalizedSalary);
    trackHomeSearch({ salaryRange });
    trackCTA({ surface: 'hero' });

    // Navigate to salary-specific page
    const url = `/${generateSalaryUrl(normalizedSalary)}`;
    navigate(url);
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSalaryInput(value);
    setIsFormatted(false);
    if (error) {
      setError(''); // Clear error when user starts typing
    }
    
    // Pass salary value to parent for sticky CTA
    if (onSalaryChange) {
      onSalaryChange(value);
    }
  };

  const handleInputBlur = () => {
    if (salaryInput && !isFormatted) {
      const normalizedSalary = normalizeSalary(salaryInput);
      if (normalizedSalary && normalizedSalary > 0) {
        const formatted = formatSalary(normalizedSalary);
        setSalaryInput(formatted);
        setIsFormatted(true);
        
        // Pass formatted value to parent
        if (onSalaryChange) {
          onSalaryChange(formatted);
        }
      }
    }
  };

  return (
    <Box
      component="form"
      role="search"
      onSubmit={handleSubmit}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        maxWidth: '500px',
        margin: '0 auto',
        width: '100%'
      }}
    >
      {/* Visible Label */}
      <Typography
        id="salary-label"
        variant="h6"
        component="label"
        sx={{
          fontWeight: 600,
          fontSize: { xs: '1rem', md: '1.1rem' },
          color: 'text.primary',
          mb: 1
        }}
      >
        Annual salary
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'stretch' }}>
        <TextField
          value={salaryInput}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          placeholder="45,000"
          size="large"
          fullWidth
          inputProps={{
            'aria-labelledby': 'salary-label',
            'aria-describedby': 'salary-help'
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Typography
                  component="span"
                  sx={{
                    color: 'action.active',
                    fontWeight: 600,
                    fontSize: { xs: '1.1rem', md: '1.2rem' }
                  }}
                >
                  £
                </Typography>
              </InputAdornment>
            ),
            sx: {
              fontSize: { xs: '1.1rem', md: '1.2rem' },
              height: '56px', // Fixed height to match button
              '& input': {
                fontSize: { xs: '1.1rem', md: '1.2rem' }
              }
            }
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              backgroundColor: 'background.paper',
              height: '56px', // Match button height
              '&:hover': {
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'primary.main'
                }
              }
            }
          }}
        />
        
        <Button
          type="submit"
          variant="contained"
          size="large"
          endIcon={<ArrowForwardIcon />}
          sx={{
            px: { xs: 4, sm: 3 },
            fontSize: { xs: '1rem', sm: '0.85rem' },
            minWidth: { xs: '100%', sm: '180px' },
            height: '56px', // Match input height
            borderRadius: 2,
            fontWeight: 600,
            boxShadow: 2,
            whiteSpace: 'nowrap', // Prevent text wrapping
            '&:hover': {
              boxShadow: 4,
              transform: 'translateY(-1px)'
            },
            transition: 'all 0.2s ease-in-out'
          }}
        >
          Calculate now
        </Button>
      </Box>

      {/* Live region for validation messages */}
      {error && (
        <Box
          role="status"
          aria-live="polite"
          sx={{
            backgroundColor: 'error.light',
            color: 'error.contrastText',
            p: 2,
            borderRadius: 1,
            fontSize: '0.9rem'
          }}
        >
          {error}
        </Box>
      )}

      {/* Supporting microcopy beneath CTA */}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          textAlign: 'center',
          fontSize: '0.8rem',
          mt: 1,
          fontWeight: 700
        }}
      >
        Takes ~1s <Box component="span" sx={{ mx: 2 }}>•</Box> No sign-up <Box component="span" sx={{ mx: 2 }}>•</Box> Nothing saved
      </Typography>

      <Typography
        variant="body2"
        color="text.secondary"
        id="salary-help"
        sx={{
          textAlign: 'center',
          fontSize: '0.875rem',
          mt: 0.5
        }}
      >
        Accepts £45,000, 45000, or 45k formats
      </Typography>
    </Box>
  );
};

export default HeroSearch;